import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AccountBoxMultiple, ChartBoxOutline } from 'mdi-material-ui';
import {
  Box,
  Button,
  Chip,
  Divider,
  Paper,
  Skeleton,
  Stack,
  Typography
} from '@mui/material';
import { CalendarToday as CalendarIcon, FitnessCenter as FitnessCenterIcon, PhotoCamera as PhotoCameraIcon } from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es as esLocale, enUS } from 'date-fns/locale';
import SessionProgressContent from 'components/session/SessionProgressContent';
import ClientProgressView from 'components/progress/physical/ClientProgressView';
import PillTabs from 'components/shared/tabs/PillTabs';
import TabPanel from 'components/shared/tabs/TabPanel';
import routes from 'common/routes';
import requestStatus from 'common/constants/status';
import { isSupervisor } from 'common/utils/user';
import performanceSlice from 'store/modules/performance';

// Orden de las pestañas. `?tab=<key>` en la URL abre una directamente: lo usan las notificaciones
// de progreso para llevar al entrenador derecho a las fotos y medidas del cliente.
const TAB_KEYS = ['performance', 'progress', 'physicalProgress'];

function ClientTabs({ client = null, loading }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, i18n } = useTranslation(['progress', 'routes', 'performances', 'buttons', 'common']);

  const clientId = Number(client?.id);
  const role = useSelector((state) => state.auth.user.role);
  const isSupervisorUser = isSupervisor(role);
  // El supervisor no tiene las pestañas de progreso, así que para él la petición se ignora.
  const requestedTab = isSupervisorUser ? -1 : TAB_KEYS.indexOf(searchParams.get('tab'));
  const [currentTab, setCurrentTab] = useState(() => Math.max(requestedTab, 0));

  // Si el enlace cambia de cliente sin desmontar la ficha, la pestaña pedida se vuelve a aplicar.
  useEffect(() => {
    if (requestedTab >= 0) setCurrentTab(requestedTab);
  }, [requestedTab]);

  const groups = useSelector((state) => state.performance.data.dynamicPerformancesByExercise);
  const listStatus = useSelector((state) => state.performance.status.listDynamic);

  useEffect(() => {
    if (clientId) {
      dispatch(performanceSlice.actions.getDynamicByExercise(clientId));
    }
  }, [clientId, dispatch]);

  const dateLocale = i18n.language === 'es' ? esLocale : enUS;

  const lastDate = useMemo(() => {
    const allDates = groups.flatMap((g) => g.performances.map((p) => p.period_start_date));
    if (!allDates.length) return null;
    return allDates.sort((a, b) => new Date(b) - new Date(a))[0];
  }, [groups]);

  const lastSessionExercises = useMemo(() => {
    if (!lastDate) return [];
    return groups
      .filter((g) => g.performances.some((p) => p.period_start_date === lastDate))
      .map((g) => g.exercise.name);
  }, [groups, lastDate]);

  const totalSessions = useMemo(() => groups.reduce((s, g) => s + g.total_count, 0), [groups]);

  const formattedDate = useMemo(() => {
    if (!lastDate) return null;
    try {
      return format(parseISO(lastDate), "d 'de' MMMM yyyy", { locale: dateLocale });
    } catch {
      return lastDate;
    }
  }, [lastDate, dateLocale]);

  const handleChange = (_, newValue) => {
    setCurrentTab(newValue);
  };

  const handleViewAll = () => {
    if (client?.id) navigate(routes.viewClientPerformance.path.replace(':id', client.id));
  };

  const isPerformanceLoading =
    listStatus === requestStatus.loading || listStatus === requestStatus.idle;

  const renderPerformancePanel = () => {
    if (loading || isPerformanceLoading) {
      return (
        <Skeleton
          variant="rectangular"
          height={160}
          sx={{ borderRadius: '16px', mx: { xs: 0, sm: 2 }, my: 1 }}
        />
      );
    }

    if (!lastDate) {
      return (
        <Box sx={{ textAlign: 'center', py: 4, color: 'text.secondary' }}>
          <FitnessCenterIcon sx={{ fontSize: 40, opacity: 0.3, mb: 1 }} />
          <Typography variant="body2">{t('empty', { ns: 'performances' })}</Typography>
        </Box>
      );
    }

    return (
      <Paper
        elevation={0}
        sx={{
          mx: { xs: 0, sm: 2 },
          my: 2,
          p: { xs: 2, md: 2.5 },
          backgroundColor: 'surface.raised',
          backgroundImage: 'none',
          border: '1px solid',
          borderColor: 'hairline.main',
          borderRadius: '16px'
        }}>
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
          <CalendarIcon sx={{ fontSize: 18, color: 'gold.main' }} />
          <Typography variant="subtitle2" sx={{ color: 'gold.main', fontWeight: 700 }}>
            {formattedDate}
          </Typography>
        </Stack>
        <Divider sx={{ borderColor: 'hairline.main', mb: 1.5 }} />
        <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mb: 2 }}>
          {lastSessionExercises.map((name) => (
            <Chip key={name} label={name} size="small" variant="outlined" />
          ))}
        </Stack>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          useFlexGap
          gap={1}>
          <Typography variant="caption" color="text.secondary">
            {totalSessions} {t('session', { count: totalSessions, ns: 'common' }).toLowerCase()} ·{' '}
            {groups.length} {t('exercise', { count: groups.length, ns: 'common' }).toLowerCase()}
          </Typography>
          <Button
            size="small"
            onClick={handleViewAll}
            sx={{
              color: 'gold.main',
              textTransform: 'none',
              fontWeight: 700,
              whiteSpace: 'nowrap',
              transition: 'transform 200ms cubic-bezier(0.32, 0.72, 0, 1)',
              '&:hover': { bgcolor: 'gold.soft' },
              '&:active': { transform: 'scale(0.98)' }
            }}>
            {t('viewAll', { ns: 'buttons', defaultValue: 'Ver todos' })}
          </Button>
        </Stack>
      </Paper>
    );
  };

  return (
    <Box sx={{ width: '100%' }}>
      {/* Los tabs de supervisor van al final del array: así los índices de los TabPanel
          (y sus ids de a11y) no se corren al ocultarlos. */}
      <PillTabs
        hasPanels
        value={currentTab}
        onChange={handleChange}
        centered
        ariaLabel={t('clientTabsLabel', { ns: 'progress', defaultValue: 'Secciones del cliente' })}
        sx={{ mb: 1 }}
        tabs={[
          {
            key: 'performance',
            label: t('performance.list', { ns: 'routes' }),
            icon: <ChartBoxOutline />
          },
          ...(isSupervisorUser ? [] : [
            {
              key: 'progress',
              label: t('progress.list', { ns: 'routes' }),
              icon: <AccountBoxMultiple />
            },
            {
              key: 'physicalProgress',
              label: t('physicalProgress', { ns: 'progress', defaultValue: 'Progreso físico' }),
              icon: <PhotoCameraIcon />
            }
          ])
        ]}
      />
      <TabPanel current={currentTab} index={0}>
        {renderPerformancePanel()}
      </TabPanel>
      {!isSupervisorUser && (
        <TabPanel current={currentTab} index={1}>
          {loading ? (
            <>
              <Skeleton height={200} />
              <Skeleton height={200} />
            </>
          ) : (
            <Box sx={{ px: { xs: 0, sm: 2 } }}>
              <SessionProgressContent clientId={clientId} />
            </Box>
          )}
        </TabPanel>
      )}
      {!isSupervisorUser && (
        <TabPanel current={currentTab} index={2}>
          {loading ? <Skeleton height={240} /> : <ClientProgressView clientId={clientId} />}
        </TabPanel>
      )}
    </Box>
  );
}

ClientTabs.propTypes = {
  client: PropTypes.object,
  loading: PropTypes.bool.isRequired
};

export default ClientTabs;
